import React, { useState, useEffect } from 'react';
import { Button } from 'antd';
import { DownloadOutlined, FileOutlined } from '@ant-design/icons';
import './AttachmentDisplay.css';
import { api } from '@redux/api';
import { getSafeUrl } from '@components/FileManagement/utils';

const AttachmentDisplay = ({ url, onImagePreview }) => {
    const [imageUrl, setImageUrl] = useState('');
    const [loading, setLoading] = useState(false);

    // 从URL中提取文件名
    const getFileName = (url) => {
        try {
            const urlParts = url.split('/');
            return urlParts[urlParts.length - 1] || '附件';
        } catch (error) {
            return '附件';
        }
    };

    // 获取文件扩展名
    const getFileExtension = (filename) => {
        const parts = filename.split('.');
        return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
    };

    // 判断是否为图片
    const isImage = (filename) => {
        const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'];
        return imageExtensions.includes(getFileExtension(filename));
    };

    // 获取真实URL
    const getRealUrl = async (url) => {
        try {
            const response = await api.generate_url(getSafeUrl(url), false);
            return response.data.url;
        } catch (error) {
            console.error('获取真实URL失败:', error);
            return url; // 降级使用原URL
        }
    };

    // 下载文件
    const handleDownload = async () => {
        setLoading(true);
        try {
            const realUrl = await getRealUrl(url);
            const link = document.createElement('a');
            link.href = realUrl;
            link.download = getFileName(url);
            link.target = '_blank';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error('下载失败:', error);
        } finally {
            setLoading(false);
        }
    };

    // 预览文件
    const handlePreview = async () => {
        if (isImageFile && onImagePreview) {
            // 图片预览使用Modal
            const realUrl = await getRealUrl(url);
            onImagePreview(realUrl);
        } else {
            // 非图片文件在新标签页打开
            const realUrl = await getRealUrl(url);
            window.open(realUrl, '_blank');
        }
    };

    // 处理图片双击预览
    const handleImageDoubleClick = async () => {
        if (onImagePreview) {
            const realUrl = await getRealUrl(url);
            onImagePreview(realUrl);
        }
    };

    const filename = getFileName(url);
    const isImageFile = isImage(filename);

    // 如果是图片，加载真实URL用于显示
    useEffect(() => {
        if (isImageFile) {
            getRealUrl(url).then(setImageUrl);
        }
    }, [url, isImageFile]);

    return (
        <div className="attachment-display">
            {isImageFile ? (
                // 图片显示模式
                <div className="attachment-image-container">
                    {imageUrl ? (
                        <img 
                            src={imageUrl} 
                            alt={filename}
                            className="attachment-image"
                            onDoubleClick={handleImageDoubleClick}
                            style={{ cursor: 'pointer' }}
                        />
                    ) : (
                        <div className="attachment-image-loading">
                            <FileOutlined />
                            <span>Loading...</span>
                        </div>
                    )}
                    <div className="attachment-image-info">
                        <div className="attachment-name" title={filename}>
                            {filename}
                        </div>
                        <div className="attachment-actions">
                            <Button 
                                type="link" 
                                size="small" 
                                icon={<DownloadOutlined />}
                                onClick={handleDownload}
                                loading={loading}
                            >
                                Download
                            </Button>
                        </div>
                    </div>
                </div>
            ) : (
                // 文件显示模式
                <div className="attachment-info">
                    <div className="attachment-icon">
                        <FileOutlined />
                    </div>
                    <div className="attachment-details">
                        <div className="attachment-name" title={filename}>
                            {filename}
                        </div>
                        <div className="attachment-actions">
                            <Button 
                                type="link" 
                                size="small" 
                                icon={<DownloadOutlined />}
                                onClick={handleDownload}
                                loading={loading}
                            >
                                Downlooad
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AttachmentDisplay; 